/**
 * Every skin hero's RASTER assets (ADR-0102) — each hero's painted base body + full-look swaps, resolved to
 * hashed, PWA-precached URLs by Vite at build (the `?url` glob). ONE cross-hero glob, mirroring how
 * `art/plugins.ts` auto-discovers every `skin.tsx`: a new skin hero is just a directory drop (`art/base.webp`
 * + `art/looks/<slug>.webp`) — no per-hero glue file. WEB-ONLY: part of the art subpath
 * (`@kidshome/hero-content/art`), never imported by the API/Node — only the web app + vitest, both via Vite,
 * so `import.meta.glob` resolves. This file keeps its own grouping (base + looks, per archetype) rather than
 * the flat `slugResolver` pets/backgrounds use, but shares `slugOf` (the glob literal must stay here).
 */
import { slugOf } from '../../art/raster';

/** A skin hero's resolved art: the bare base figure + its `slug → url` full-look swaps. */
export type HeroSkinAssets = {
  readonly base: string;
  readonly looks: Readonly<Record<string, string>>;
};

const EMPTY: HeroSkinAssets = { base: '', looks: {} };

const BASES = import.meta.glob('./*/art/base.webp', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

const LOOKS = import.meta.glob('./*/art/looks/*.webp', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

/** The archetype slug is the hero's directory name: `./<archetype>/art/…`. */
function archetypeOf(path: string): string {
  return path.split('/')[1] ?? '';
}

const ASSETS: Record<string, { base: string; looks: Record<string, string> }> = {};
const ensure = (arch: string) => (ASSETS[arch] ??= { base: '', looks: {} });

for (const [path, url] of Object.entries(BASES)) {
  ensure(archetypeOf(path)).base = url;
}
for (const [path, url] of Object.entries(LOOKS)) {
  const slug = slugOf(path);
  if (slug) ensure(archetypeOf(path)).looks[slug] = url;
}

/** A skin hero's art (base + looks), or the empty pair for a hero with no raster art (honest blank figure). */
export function heroSkinAssetsFor(archetypeId: string): HeroSkinAssets {
  return ASSETS[archetypeId] ?? EMPTY;
}

/**
 * Every archetype that ships a painted BASE figure — i.e. every "skin" hero (ADR-0116). `art/plugins.ts`
 * builds the shared skin renderer for each of these that has no bespoke `skin.tsx`, so a plain skin hero is a
 * directory drop (drop `art/`, get a rendered hero) with no per-hero render code.
 */
export const SKIN_ARCHETYPES: readonly string[] = Object.entries(ASSETS)
  .filter(([, a]) => a.base !== '')
  .map(([archetype]) => archetype);
